import {
  faBug,
  faExclamationTriangle,
  faHome,
  faLock,
  faRedo,
  faRefresh,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useLanguage } from "../contexts/LanguageContext";

import "./ErrorPage.css";

const ErrorPage = ({
  errorCode = "404",
  error,
  errorInfo,
  errorId,
  retryCount = 0,
  onRetry,
  onReload,
  onReport,
  maxRetries = 3,
}) => {
  const { t } = useLanguage();

  const handleGoHome = () => {
    window.location.href = "/";
  };

  const isJavaScriptError = errorCode === "500" && (error || errorInfo);
  const canRetry = isJavaScriptError && retryCount < maxRetries && onRetry;

  const getErrorIcon = () => {
    switch (errorCode) {
      case "404":
        return <FontAwesomeIcon icon={faSearch} />;
      case "500":
        return <FontAwesomeIcon icon={faExclamationTriangle} />;
      case "403":
        return <FontAwesomeIcon icon={faLock} />;
      default:
        return <FontAwesomeIcon icon={faExclamationTriangle} />;
    }
  };

  const getErrorDetails = () => {
    switch (errorCode) {
      case "404":
        return {
          title: t("errorPage.error404"),
          message: t("errorPage.error404Message"),
          suggestion: t("errorPage.error404Suggestion"),
        };
      case "500":
        return {
          title: isJavaScriptError
            ? t("errorPage.jsError")
            : t("errorPage.error500"),
          message: isJavaScriptError
            ? t("errorPage.jsErrorMessage")
            : t("errorPage.error500Message"),
          suggestion: isJavaScriptError
            ? t("errorPage.jsErrorSuggestion")
            : t("errorPage.error500Suggestion"),
        };
      case "403":
        return {
          title: t("errorPage.error403"),
          message: t("errorPage.error403Message"),
          suggestion: t("errorPage.error403Suggestion"),
        };
      default:
        return {
          title: t("errorPage.defaultTitle"),
          message: t("errorPage.defaultMessage"),
          suggestion: t("errorPage.defaultSuggestion"),
        };
    }
  };

  const errorDetails = getErrorDetails();

  return (
    <div className="error-page">
      <div className="error-content">
        <div className="container">
          <div className="error-card">
            <div className="error-icon">{getErrorIcon()}</div>
            <div className="error-code">{errorCode}</div>
            <h1 className="error-title">{errorDetails.title}</h1>
            <p className="error-message">{errorDetails.message}</p>
            <p className="error-suggestion">{errorDetails.suggestion}</p>

            {/* Error Details for JavaScript Errors */}
            {isJavaScriptError && (
              <div className="error-details">
                {errorId && (
                  <p className="error-id">
                    <strong>{t("errorPage.errorId")}:</strong> {errorId}
                  </p>
                )}
                {retryCount > 0 && (
                  <p className="retry-count">
                    <strong>{t("errorPage.retryAttempts")}:</strong>{" "}
                    {retryCount}/{maxRetries}
                  </p>
                )}
                {process.env.NODE_ENV === "development" && error && (
                  <details className="error-stack">
                    <summary>{t("errorPage.technicalDetails")}</summary>
                    <pre className="error-stack-trace">
                      <code>
                        {error.message && `Message: ${error.message}\n\n`}
                        {error.stack || error.toString()}
                      </code>
                    </pre>
                  </details>
                )}
              </div>
            )}

            <div className="error-actions">
              {canRetry && (
                <button onClick={onRetry} className="btn btn-secondary">
                  <FontAwesomeIcon icon={faRedo} />
                  {t("errorPage.tryAgain")}
                </button>
              )}

              {isJavaScriptError && onReload && (
                <button onClick={onReload} className="btn btn-warning">
                  <FontAwesomeIcon icon={faRefresh} />
                  {t("errorPage.reloadPage")}
                </button>
              )}

              {isJavaScriptError && onReport && (
                <button onClick={onReport} className="btn btn-info">
                  <FontAwesomeIcon icon={faBug} />
                  {t("errorPage.reportError")}
                </button>
              )}

              <button onClick={handleGoHome} className="btn btn-primary">
                <FontAwesomeIcon icon={faHome} />
                {t("errorPage.goHome")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorPage;
